<template>
  <v-card>
    <v-card-text>
      <div class="d-flex justify-space-between align-center ml-4 mt-3 mr-4">
        <div class="d-flex flex-column">
          <span
            class="subtitle1"
            v-text="$t('network.secureWebGateway.conditionalForwarding.title')"
          />
          <span
            class="body2 text-indigo-medium"
            v-text="$t('network.secureWebGateway.conditionalForwarding.description')"
          />
        </div>
        <v-btn
          v-if="items.length === 0"
          class="add-btn"
          color="primary"
          rounded
          large
          :disabled="loading"
          data-testid="conditional-forwarding-add-btn"
          @click="onAddClick"
        >
          <v-icon icon="$add" color="white" />
          <span v-text="$t('general.add')" />
        </v-btn>
      </div>
      <conditional-forwarding-table
        v-if="items.length > 0"
        class="mt-4"
        :items="items"
        :loading="loading"
        @action-applied="onAction($event)"
      />
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, onMounted } from "vue";
import { storeToRefs } from "pinia";
import ConditionalForwardingTable from "@/components/tables/ConditionalForwardingTable.vue";
import {
  type ConditionalForwardingItem,
  useConditionalForwardingStore,
} from "@/_store/network/swg/conditional-forwarding.module.ts";
import { useDialogsStore } from "@/_store/dialogs.module.ts";
import AddOrEditConditionalForwardingModal from "@/components/modals/network/AddOrEditConditionalForwardingModal.vue";
import { ModalWidth } from "@/constants/modals.ts";
import { ConditionalForwardingAction } from "@/constants/network.ts";
import {
  componentDialogsConfigConstructor,
  confirmationDialogsConfigConstructor,
} from "@/_helpers/dialog-helpers.ts";

export default defineComponent({
  components: {
    ConditionalForwardingTable,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const conditionalForwardingStore = useConditionalForwardingStore();
    const dialogsStore = useDialogsStore();
    const { items } = storeToRefs(conditionalForwardingStore);
    const { getItems, addItem, updateItem, deleteItem } = conditionalForwardingStore;

    const onAddClick = () => {
      dialogsStore.openDialog({
        ...componentDialogsConfigConstructor({
          action: ConditionalForwardingAction.ADD,
          component: AddOrEditConditionalForwardingModal,
          width: ModalWidth.LARGE,
          callback: async (payload: Omit<ConditionalForwardingItem, "id">) => {
            await addItem(payload);
          },
        }),
      });
    };

    const onAction = ({
      action,
      item,
    }: {
      action: ConditionalForwardingAction;
      item: ConditionalForwardingItem;
    }) => {
      if (action === ConditionalForwardingAction.EDIT) {
        dialogsStore.openDialog({
          ...componentDialogsConfigConstructor({
            action: ConditionalForwardingAction.EDIT,
            component: AddOrEditConditionalForwardingModal,
            width: ModalWidth.LARGE,
            item,
            callback: async (payload: Omit<ConditionalForwardingItem, "id">) => {
              await updateItem(item.id, payload);
            },
          }),
        });
      } else if (action === ConditionalForwardingAction.REMOVE) {
        dialogsStore.openDialog({
          ...confirmationDialogsConfigConstructor({
            item,
            action: ConditionalForwardingAction.REMOVE,
            callback: async () => {
              await deleteItem(item.id);
            },
            width: ModalWidth.SMALL,
          }),
        });
      }
    };

    onMounted(async () => {
      await getItems();
    });

    return {
      items,
      onAddClick,
      onAction,
    };
  },
});
</script>

<style scoped lang="scss"></style>
